import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    redirect: to => ({ path: '/admin', query: to.query }),
  },
  {
    path: '/admin',
    name: 'AdminLogin',
    component: () => import('@/views/AdminLogin.vue'),
    meta: { guest: true }
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/my-uploads'
      },
      {
        path: 'dashboard',
        redirect: '/my-uploads'
      },
      {
        path: 'upload',
        name: 'Upload',
        component: () => import('@/views/Upload.vue'),
        meta: { permission: 'photo_upload' }
      },
      {
        path: 'my-uploads',
        name: 'MyUploads',
        component: () => import('@/views/MyUploads.vue'),
        meta: { permission: 'my_uploads' }
      },
      {
        path: 'search',
        name: 'Search',
        component: () => import('@/views/SearchPhoto.vue'),
        meta: { permission: 'photo_search' }
      },
      {
        path: 'download-history',
        name: 'DownloadHistory',
        component: () => import('@/views/DownloadHistory.vue'),
        meta: { permission: 'download_history' }
      },
      {
        path: 'upload-stats',
        name: 'UploadStats',
        component: () => import('@/views/UploadStats.vue'),
        meta: { permission: 'upload_stats' }
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/views/Users.vue'),
        meta: { permission: 'user_management' }
      },
      {
        path: 'roles',
        name: 'Roles',
        component: () => import('@/views/Roles.vue'),
        meta: { permission: 'role_management' }
      },
      {
        path: 'menus',
        name: 'MenuManagement',
        component: () => import('@/views/MenuManagement.vue'),
        meta: { permission: 'menu_management' }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/Settings.vue'),
        meta: { permission: 'system_settings' }
      },
      {
        path: 'prompt-template',
        name: 'PromptTemplate',
        component: () => import('@/views/PromptTemplate.vue'),
        meta: { permission: 'prompt_template' }
      },
      {
        path: 'competitor-analysis',
        name: 'CompetitorAnalysis',
        component: () => import('@/views/CompetitorAnalysis.vue'),
        meta: { permission: 'competitor_analysis' }
      },
      {
        path: 'ai-logs',
        name: 'AiGenerationLogs',
        component: () => import('@/views/AiGenerationLogs.vue'),
        meta: { permission: 'ai_generation_logs' }
      },
      {
        path: 'folder-management',
        name: 'FolderManagement',
        component: () => import('@/views/FolderManagement.vue'),
        meta: { permission: 'folder_management' }
      },
      {
        path: 'action-logs',
        name: 'ActionLogs',
        component: () => import('@/views/ActionLogs.vue'),
        meta: { permission: 'action_logs' }
      },
      {
        path: 'profile-settings',
        name: 'ProfileSettings',
        component: () => import('@/views/ProfileSettings.vue'),
      },
    ]
  },
  {
    path: '*',
    redirect: '/'
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

const fallbackRoutesByPermission = [
  { permission: 'my_uploads', route: { name: 'MyUploads' } },
  { permission: 'photo_upload', route: { name: 'Upload' } },
  { permission: 'photo_search', route: { name: 'Search' } },
  { permission: 'competitor_analysis', route: { name: 'CompetitorAnalysis' } },
  { permission: 'upload_stats', route: { name: 'UploadStats' } },
  { permission: 'download_history', route: { name: 'DownloadHistory' } },
]

function getAuthenticatedFallbackRoute(userPermissions = []) {
  const match = fallbackRoutesByPermission.find(item => userPermissions.includes(item.permission))
  return match ? match.route : { name: 'ProfileSettings' }
}

// Suppress NavigationDuplicated and NavigationCancelled errors
const IGNORED_NAV_ERRORS = ['NavigationDuplicated', 'NavigationCancelled']

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (!IGNORED_NAV_ERRORS.includes(err.name)) {
      throw err
    }
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (!IGNORED_NAV_ERRORS.includes(err.name)) {
      throw err
    }
  })
}

// Navigation guard
router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const userPermissions = store.getters['auth/permissions'] || []
  
  // Check if route or its parent requires auth
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isGuestRoute = to.matched.some(record => record.meta.guest)
  
  // Guest only routes (login page)
  if (isGuestRoute && isAuthenticated) {
    return next(getAuthenticatedFallbackRoute(userPermissions))
  }
  
  // Protected routes - redirect to admin login if not authenticated
  if (requiresAuth && !isAuthenticated) {
    if (to.name === 'AdminLogin') {
      return next()
    }
    return next({ name: 'AdminLogin' })
  }
  
  // Permission check
  if (to.meta.permission && isAuthenticated) {
    if (!userPermissions.includes(to.meta.permission)) {
      const fallbackRoute = getAuthenticatedFallbackRoute(userPermissions)
      if (to.name === fallbackRoute.name) {
        return next()
      }
      return next(fallbackRoute)
    }
  }
  
  next()
})

export default router
